import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'

type SidebarContextValue = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  state: 'expanded' | 'collapsed'
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)

  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.')
  }

  return context
}

type SidebarProviderProps = React.ComponentProps<'div'> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  ({ children, className, defaultOpen = true, open: openProp, onOpenChange, style, ...props }, ref) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
    const open = openProp ?? uncontrolledOpen

    const setOpen = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
      (value) => {
        const nextValue = typeof value === 'function' ? value(open) : value

        if (openProp === undefined) {
          setUncontrolledOpen(nextValue)
        }

        onOpenChange?.(nextValue)
      },
      [open, openProp, onOpenChange]
    )

    return (
      <SidebarContext.Provider value={{ open, setOpen, state: open ? 'expanded' : 'collapsed' }}>
        <div ref={ref} className={cn('group/sidebar-wrapper min-h-0 w-full', className)} style={style} {...props}>
          {children}
        </div>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = 'SidebarProvider'

type SidebarProps = React.ComponentProps<'aside'> & {
  collapsible?: 'icon' | 'none'
  collapsedClassName?: string
}

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  ({ children, className, collapsible = 'icon', collapsedClassName, ...props }, ref) => {
    const { state } = useSidebar()

    return (
      <aside
        ref={ref}
        data-collapsible={collapsible}
        data-slot="sidebar"
        data-state={state}
        className={cn(
          'group/sidebar flex h-full min-h-0 shrink-0 flex-col overflow-hidden border-r bg-background transition-[width] duration-300 ease-in-out will-change-[width] motion-reduce:transition-none',
          collapsible === 'icon' && state === 'collapsed'
            ? 'w-[var(--sidebar-width-icon,55px)] ' + collapsedClassName
            : 'w-[var(--sidebar-width,280px)]',
          className
        )}
        {...props}
      >
        {children}
      </aside>
    )
  }
)
Sidebar.displayName = 'Sidebar'

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="sidebar-content" className={cn('flex min-h-0 flex-1 flex-col', className)} {...props} />
))
SidebarContent.displayName = 'SidebarContent'

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({ className, ...props }, ref) => (
  <ul ref={ref} data-slot="sidebar-menu" className={cn('flex min-w-0 flex-col gap-1', className)} {...props} />
))
SidebarMenu.displayName = 'SidebarMenu'

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} data-slot="sidebar-menu-item" className={cn('group/sidebar-menu-item relative list-none', className)} {...props} />
))
SidebarMenuItem.displayName = 'SidebarMenuItem'

type SidebarMenuButtonProps = React.ComponentProps<'button'> & {
  asChild?: boolean
  isActive?: boolean
}

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ asChild = false, className, isActive = false, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        data-active={isActive}
        data-slot="sidebar-menu-button"
        type={asChild ? undefined : type}
        className={cn(
          'flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-3 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          'hover:bg-transparent hover:text-brand-hover data-[active=true]:bg-transparent data-[active=true]:text-foreground',
          className
        )}
        {...props}
      />
    )
  }
)
SidebarMenuButton.displayName = 'SidebarMenuButton'

const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({ className, ...props }, ref) => (
  <ul ref={ref} data-slot="sidebar-menu-sub" className={cn('flex min-w-0 flex-col gap-1', className)} {...props} />
))
SidebarMenuSub.displayName = 'SidebarMenuSub'

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} data-slot="sidebar-menu-sub-item" className={cn('list-none', className)} {...props} />
))
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem'

type SidebarMenuSubButtonProps = React.ComponentProps<'button'> & {
  asChild?: boolean
  isActive?: boolean
}

const SidebarMenuSubButton = React.forwardRef<HTMLButtonElement, SidebarMenuSubButtonProps>(
  ({ asChild = false, className, isActive = false, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        data-active={isActive}
        data-slot="sidebar-menu-sub-button"
        type={asChild ? undefined : type}
        className={cn(
          'flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-3 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          'hover:bg-transparent hover:text-brand-hover data-[active=true]:bg-transparent data-[active=true]:text-foreground',
          className
        )}
        {...props}
      />
    )
  }
)
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton'

type SidebarTriggerProps = React.ComponentProps<typeof Button>

const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, onClick, type = 'button', ...props }, ref) => {
    const { open, setOpen } = useSidebar()

    return (
      <Button
        ref={ref}
        type={type}
        variant="ghost"
        className={cn(className)}
        onClick={(event) => {
          onClick?.(event)
          if (!event.defaultPrevented) {
            setOpen(!open)
          }
        }}
        {...props}
      />
    )
  }
)
SidebarTrigger.displayName = 'SidebarTrigger'

export {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
}
